import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";
import JsonLd from "@/components/JsonLd";
import { IMAGES, PLUGGY_VIDEO_URL } from "@/data/brand";
import {
  ArrowRight, ShieldCheck, Flame, Users, Award, Sparkles, CheckCircle2,
  Truck, Building2, BadgeCheck, Radio, HeartHandshake, PlayCircle,
} from "lucide-react";

const VALUE_POINTS = [
  { icon: Flame, title: "Field-tested by a firefighter", desc: "I don't just sell these robots — I run them in live school assemblies every month. You're buying insight from someone who's stood in the gym, mic in hand, and watched what actually lands with a room full of kids." },
  { icon: Users, title: "Real-world engagement data", desc: "I know which features hold a kindergartner's attention, how to script a hydrant demo, and what makes a lesson stick. That operational know-how ships with every unit." },
  { icon: BadgeCheck, title: "An authorized partner", desc: "I hold an official sales and compensation agreement with the manufacturer, so you get legitimate product, warranty-backed support, and a direct line to someone who uses the exact same gear." },
];

const PLUGGY_FEATURES = [
  "Interactive, remote-operated fire hydrant character kids instantly love",
  "Turns a routine safety talk into a memorable event families talk about",
  "Positions your department as innovative, approachable, and community-first",
  "Reinforces core messages — Stop, Drop & Roll, escape plans, 9-1-1 — through play",
];

const TIERS = [
  {
    name: "Standard Package",
    tagline: "Pluggie the Fire Plug® Robot",
    item: "Item# 100012",
    icon: Radio,
    blurb: "Pluggie the Fire Plug® is Robotronics' first robot and is still very popular with kids today. Pluggie is an exciting tool for teaching fire safety. Audiences of all venues will be delighted when Pluggie's eyes light up, his cap rises and he moves around the room carrying on conversations and playing music, all by remote control. The standard robot comes with everything you need to operate it.",
    perks: ["Pluggie the Fire Plug® robot", "Full remote-control operation", "Light-up eyes & rising cap", "Onboard conversations & music playback", "Everything needed to operate, out of the box"],
    price: "$10,389.00",
    note: "Item# 100012",
  },
  {
    name: "Premium Package",
    tagline: "Pluggie the Fire Plug® Robot PACKAGE",
    item: "Item# 100013",
    icon: Truck,
    featured: true,
    blurb: "Everything in the Standard Package plus the accessories every working team needs to transport, protect and keep Pluggie running all day — a $164.00 savings over buying the items separately.",
    perks: ["Everything in the Standard Package", "Extra RC Battery Pack", "Robot Transport Cart", "Robot Cover", "Extra Robot Battery", "$164.00 savings vs. buying separately"],
    price: "$11,089.00",
    note: "Item# 100013 · Best value",
  },
];

function toEmbedUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}?autoplay=1`;
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
      return url.replace("/watch", "/embed");
    }
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.split("/").pop()}?autoplay=1`;
    return url;
  } catch {
    return url;
  }
}

function VideoReel() {
  const [playing, setPlaying] = useState(false);
  const isFile = /\.(mp4|webm|ogg)$/i.test(PLUGGY_VIDEO_URL || "");

  const handlePlay = () => {
    if (!PLUGGY_VIDEO_URL) {
      toast("Demo reel coming soon — the assembly clip will play right here.");
      return;
    }
    setPlaying(true);
  };

  return (
    <section className="bg-sq-ink text-white py-24">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-yellow mb-3">See it before you buy</p>
          <h2 className="font-display text-5xl lg:text-7xl tracking-tight leading-[0.9]">See Pluggy in action</h2>
          <p className="font-body text-lg text-white/70 mt-4 max-w-2xl">
            Watch how a single robotic hydrant turns a routine safety talk into the assembly students
            talk about all year. The fastest way to picture Pluggy at your next department event.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 relative rounded-[2rem] overflow-hidden border-4 border-white/10 aspect-video bg-black">
            {playing && PLUGGY_VIDEO_URL ? (
              isFile ? (
                <video src={PLUGGY_VIDEO_URL} controls autoPlay className="w-full h-full object-cover" data-testid="pluggy-video-player" />
              ) : (
                <iframe
                  src={toEmbedUrl(PLUGGY_VIDEO_URL)}
                  title="Pluggy in action"
                  data-testid="pluggy-video-player"
                  className="w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                />
              )
            ) : (
              <button onClick={handlePlay} data-testid="pluggy-video-play" className="group absolute inset-0 w-full h-full">
                <img src={IMAGES.roboticsAssembly} alt="Pluggy robot at a live fire-safety assembly" className="w-full h-full object-cover opacity-70 group-hover:opacity-80 transition-opacity" />
                <div className="absolute inset-0 grid place-items-center">
                  <div className="flex flex-col items-center gap-4">
                    <span className="grid place-items-center w-20 h-20 rounded-full bg-sq-red text-white shadow-2xl group-hover:scale-110 transition-transform">
                      <PlayCircle size={40} />
                    </span>
                    <span className="font-round text-lg font-semibold bg-black/50 backdrop-blur px-5 py-2 rounded-full">
                      {PLUGGY_VIDEO_URL ? "Play the 60-second demo" : "Demo reel coming soon"}
                    </span>
                  </div>
                </div>
              </button>
            )}
          </div>
          <p className="text-white/40 text-sm mt-4 flex items-center gap-2">
            <Sparkles size={14} className="text-sq-yellow" /> 30–60 second field footage from a real school assembly.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Robotics() {
  useSEO({
    title: "Buy Pluggy the Robotic Fire Hydrant — Sales & Partnerships",
    description: "Fire departments, safety coordinators and school districts: upgrade your community outreach with Pluggy, the interactive robotic fire hydrant, purchased through a field-tested firefighter and authorized Robotronics partner.",
  });

  return (
    <>
      <JsonLd id="pluggie-product" data={{
        "@context": "https://schema.org",
        "@type": "Product",
        name: "Pluggie the Fire Plug® Robot",
        description: "Interactive remote-controlled robotic fire hydrant for fire-safety education. Eyes light up, cap rises, moves around the room, holds conversations and plays music.",
        brand: { "@type": "Brand", name: "Robotronics" },
        category: "Safety Education Robotics",
        image: IMAGES.roboticsAssembly,
        offers: { "@type": "AggregateOffer", availability: "https://schema.org/InStock", priceCurrency: "USD" },
      }} />
      {/* Hero */}
      <section className="pt-36 pb-20 bg-sq-ink text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-xs font-bold uppercase tracking-[0.15em] mb-6">
              <ShieldCheck size={14} className="text-sq-yellow" /> Authorized Robotronics Sponsor-Partner
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-[5.5rem] leading-[0.85] tracking-tight">
              Turn your next school visit into an <span className="text-sq-red">unforgettable</span> safety event.
            </h1>
            <p className="font-body text-lg text-white/70 mt-6 max-w-xl">
              Interactive robotics are changing how departments connect with kids. Meet Pluggy —
              the robotic fire hydrant that transforms standard fire-prevention talks into the
              assembly students remember all year. Purchased through a firefighter who uses it in the field.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="robotics-hero-cta" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-sq-yellow text-sq-ink font-bold text-lg btn-chunky">
                Request a Custom Quote <ArrowRight size={20} />
              </Link>
              <a href="#packages" data-testid="robotics-hero-packages" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/10 text-white font-bold text-lg border border-white/20 hover:bg-white/15 transition-colors">
                View Packages
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-sq-red rounded-[2rem] rotate-2" />
              <img src={IMAGES.roboticsAssembly} alt="Pluggy robot at a live school fire-safety assembly" className="relative rounded-[1.8rem] w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-sq-ink/10 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Flame, t: "Field-tested", d: "Used in live school assemblies" },
            { icon: BadgeCheck, t: "Warranty-backed", d: "Supported through Robotronics" },
            { icon: Award, t: "Official partner", d: "Sales & compensation agreement" },
            { icon: Users, t: "Trusted by pros", d: "Fire departments & school districts" },
          ].map((s) => (
            <div key={s.t} data-testid={`trust-${s.t.toLowerCase().replace(/[^a-z]/g, "")}`} className="flex items-center gap-3">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-sq-red/10 text-sq-red shrink-0"><s.icon size={22} /></span>
              <div>
                <p className="font-round font-semibold leading-tight">{s.t}</p>
                <p className="text-sq-ink/55 text-sm leading-tight">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Value Proposition */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">Why buy through my platform</p>
          <h2 className="font-display text-5xl lg:text-7xl tracking-tight max-w-3xl leading-[0.9]">
            Insight from someone who runs the gear — not just sells it.
          </h2>
          <p className="font-body text-lg text-sq-ink/70 mt-5 max-w-2xl">
            Plenty of vendors can ship you a box. Very few have stood in front of 200 first-graders and
            made a fire hydrant the star of the show. As a former firefighter running live, interactive
            assemblies, I bought in because it works — and now I help other departments do the same.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {VALUE_POINTS.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div data-testid={`value-${i}`} className="h-full rounded-3xl bg-white border border-sq-ink/10 p-8 hover:shadow-xl transition-shadow">
                <div className="grid place-items-center w-14 h-14 rounded-2xl bg-sq-red/10 text-sq-red mb-6"><v.icon size={26} /></div>
                <h3 className="font-round text-2xl font-semibold">{v.title}</h3>
                <p className="text-sq-ink/70 mt-3 leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pluggy Sales Pitch */}
      <section className="bg-sq-red text-white py-24 overflow-hidden relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-yellow mb-3">Meet your new outreach MVP</p>
            <h2 className="font-display text-5xl lg:text-6xl tracking-tight leading-[0.9]">Why Pluggy earns its place in your budget</h2>
            <p className="text-white/90 text-lg mt-5 leading-relaxed">
              The moment Pluggy rolls into a gymnasium, the energy in the room changes. Kids lean in,
              phones come out, and suddenly your department isn't giving a lecture — you're putting on
              the event of the semester. That engagement is what makes the safety lessons actually
              stick: children remember what to do in an emergency because they remember the hydrant who
              taught them. For your department, that means stronger school relationships, standout local
              PR, and a reputation as the innovative team that shows up and makes safety fun. One unit
              does the work of a dozen flyers.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 mt-8">
              {PLUGGY_FEATURES.map((f) => (
                <li key={f} className="flex gap-2 text-white/90"><CheckCircle2 size={20} className="text-sq-yellow shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Video reel */}
      <VideoReel />

      {/* Packages */}
      <section id="packages" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 scroll-mt-24">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">Purchasing & Partnership Packages</p>
          <h2 className="font-display text-5xl lg:text-7xl tracking-tight">Pick the setup that fits your team</h2>
          <p className="font-body text-lg text-sq-ink/70 mt-4 max-w-2xl">
            Two ways to bring Pluggie to your community. Choose the standard robot or the full
            package with everything you need to transport, protect and power your outreach.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 mt-14 items-start max-w-4xl mx-auto">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div data-testid={`package-${i}`} className={`h-full rounded-3xl p-8 border-2 ${t.featured ? "bg-sq-ink text-white border-sq-ink shadow-[0_10px_0_#DC2626]" : "bg-white border-sq-ink/15"}`}>
                {t.featured && <span className="inline-block mb-4 text-xs font-bold uppercase tracking-widest bg-sq-yellow text-sq-ink px-3 py-1 rounded-full">Best Value</span>}
                <div className={`grid place-items-center w-12 h-12 rounded-xl mb-5 ${t.featured ? "bg-white/10 text-sq-yellow" : "bg-sq-red/10 text-sq-red"}`}><t.icon size={24} /></div>
                <h3 className="font-round text-2xl font-semibold">{t.name}</h3>
                <p className={`text-sm mt-1 ${t.featured ? "text-white/60" : "text-sq-ink/50"}`}>{t.tagline}</p>
                <p className={`font-display text-5xl tracking-wide mt-4 ${t.featured ? "text-sq-yellow" : "text-sq-red"}`}>{t.price}</p>
                <p className={`text-xs mt-1 ${t.featured ? "text-white/50" : "text-sq-ink/40"}`}>{t.note}</p>
                <p className={`mt-5 text-sm leading-relaxed ${t.featured ? "text-white/70" : "text-sq-ink/70"}`}>{t.blurb}</p>
                <ul className="mt-5 space-y-2.5">
                  {t.perks.map((p) => (
                    <li key={p} className={`flex gap-2 text-sm ${t.featured ? "text-white/80" : "text-sq-ink/75"}`}>
                      <CheckCircle2 size={18} className={`shrink-0 mt-0.5 ${t.featured ? "text-sq-yellow" : "text-sq-red"}`} /> {p}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" data-testid={`package-cta-${i}`} className={`mt-7 inline-flex w-full justify-center items-center gap-2 px-5 py-3.5 rounded-full font-bold ${t.featured ? "bg-sq-yellow text-sq-ink" : "bg-sq-red text-white"} btn-chunky`}>
                  Request Details <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Add-on options */}
        <Reveal delay={0.1}>
          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            <div data-testid="addon-voice" className="rounded-3xl bg-sq-blue/5 border border-sq-blue/20 p-7">
              <div className="grid place-items-center w-12 h-12 rounded-xl bg-sq-blue/10 text-sq-blue mb-4"><Radio size={22} /></div>
              <h4 className="font-round text-xl font-semibold">Add-on · Voice Modifier</h4>
              <p className="text-sq-ink/70 mt-2 text-sm leading-relaxed">Changes the operator's voice coming out of the robot into a fun, cartoony robot voice that kids instantly love.</p>
            </div>
            <div data-testid="addon-squirter" className="rounded-3xl bg-sq-red/5 border border-sq-red/20 p-7">
              <div className="grid place-items-center w-12 h-12 rounded-xl bg-sq-red/10 text-sq-red mb-4"><Flame size={22} /></div>
              <h4 className="font-round text-xl font-semibold">Add-on · Water Squirter</h4>
              <p className="text-sq-ink/70 mt-2 text-sm leading-relaxed">Lets Pluggie shoot a stream of water 15–20 feet — a guaranteed crowd-pleaser at any event.</p>
            </div>
          </div>
          <p className="text-sq-ink/50 text-sm mt-5">
            Add the robot to your quote first, then choose your add-on options. Both upgrades are available on any package.
          </p>
        </Reveal>
      </section>

      {/* Full Robotronics Lineup */}
      <section className="bg-sq-ink text-white py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-yellow mb-3">Beyond Pluggie</p>
              <h2 className="font-display text-5xl lg:text-6xl tracking-tight leading-[0.9]">The full Robotronics lineup</h2>
              <p className="font-body text-lg text-white/70 mt-5 max-w-xl">
                Pluggie is just the beginning. As a Robotronics sponsor-partner, I can help your
                department or district source the entire lineup — interactive robotic vehicles and
                costumed safety mascots that make every outreach event unforgettable.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { icon: Truck, t: "Interactive Robotic Vehicles", d: "Remote-controlled Fire Truck, Ambulance, Police Car and School Bus — talking, moving, light-up favorites." },
                  { icon: Users, t: "Costumed Safety Mascots", d: "Fire-safety dogs, safety bears and more — full-body character costumes for assemblies and parades." },
                  { icon: Radio, t: "Character Robots", d: "Pluggie the Fire Plug® and friends — remote-operated robots that talk, play music and delight any crowd." },
                ].map((c) => (
                  <div key={c.t} className="flex gap-4">
                    <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white/10 text-sq-yellow shrink-0"><c.icon size={22} /></span>
                    <div>
                      <h3 className="font-round text-xl font-semibold">{c.t}</h3>
                      <p className="text-white/60 text-sm mt-1 leading-relaxed">{c.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/contact" data-testid="lineup-catalog-cta" className="inline-flex items-center gap-2 mt-8 px-7 py-4 rounded-full bg-sq-yellow text-sq-ink font-bold text-lg btn-chunky">
                Request the Full Catalog <ArrowRight size={20} />
              </Link>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -inset-3 bg-sq-red rounded-[2rem] -rotate-2" />
                <img src={IMAGES.roboticsLineup} alt="The full Robotronics lineup of safety mascots and interactive robotic vehicles" className="relative rounded-[1.8rem] w-full object-cover bg-white" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-sq-blue text-white p-10 lg:p-16 text-center">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          <div className="relative max-w-2xl mx-auto">
            <HeartHandshake className="mx-auto text-sq-yellow" size={44} />
            <h2 className="font-display text-5xl lg:text-7xl tracking-tight mt-4 leading-[0.9]">Let's talk about your outreach program</h2>
            <p className="font-round text-lg mt-4 text-white/90">
              Whether you're buying a single unit or planning a district-wide fleet, reach out to discuss
              purchasing options, request a custom quote, or see how Pluggy fits into your annual budget.
              One safety professional to another — I'll walk you through exactly what to expect.
            </p>
            <Link to="/contact" data-testid="robotics-final-cta" className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-sq-yellow text-sq-ink font-bold text-lg btn-chunky">
              Start the Conversation <ArrowRight size={20} />
            </Link>
            <p className="text-white/60 text-sm mt-5 flex items-center justify-center gap-2">
              <Award size={16} className="text-sq-yellow" /> Authorized Robotronics partner · Field-tested by a former firefighter
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
